import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../../lib/i18n";
import { useSettings } from "../../lib/queries";
import { optimizeImg } from "../../lib/img";

export default function About() {
  const { data: settings } = useSettings();
  const { t, lang, pick } = useLang();
  if (!settings) return null;

  const values = lang === "tr" ? settings.values_tr : settings.values_en;

  return (
    <section id="about" data-testid="about-section" className="py-32 relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#A855F7]" />
              <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase">
                {t("about_kicker")}
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white leading-[0.95]"
            >
              {pick(settings, "about_title")}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-10 text-white/60 max-w-2xl text-lg leading-relaxed"
            >
              {pick(settings, "about_body")}
            </motion.p>

            <div className="mt-14">
              <h4 className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] mb-6 uppercase">
                {t("about_values")}
              </h4>
              <div className="flex flex-wrap gap-3">
                {values.map((v, i) => (
                  <motion.span
                    key={v}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06, duration: 0.4 }}
                    className="px-5 py-2.5 rounded-full border border-white/10 text-white/80 text-sm hover:border-[#A855F7]/60 hover:text-white transition-colors"
                  >
                    {v}
                  </motion.span>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden aspect-[4/5] border border-white/5"
            >
              <img
                src={optimizeImg(settings.about_image, 900, 75)}
                alt="Studio"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080D] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 font-mono-ui text-[10px] tracking-widest uppercase text-white/70 flex justify-between">
                <span>Studio · Gaziantep</span>
                <span className="text-[#A855F7]">Est. 2018</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
